import { useMemo, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Pencil, Power, Search, Send, Users } from 'lucide-react';
import { cn } from '../../../../shared';
import { personLabel } from './personLabel';
import { LinkedInLinkActions } from './LinkedInLinkActions';
import { roleName, usePostingTitles } from './usePostingTitles';
import type { InterviewLink } from '../../services/interviewLinksApi';

/**
 * FINN's Lever stage ids in pipeline order — a role's links are a sequence the TA
 * manager reads top to bottom. Ranked by ID, not name: "Case Interview" and
 * "Case / Technical Interview" are different strings for the same stage.
 * Mirrors STAGE_PIPELINE in proxy-server/lib/interviewLinks.js.
 */
const STAGE_PIPELINE = [
  'lead-new',
  'lead-reached-out',
  'lead-responded',
  'applicant-new',
  'c0392813-c1de-42ff-bc02-11987cf4c57d',
  '60a189ad-dad3-4dbd-a376-b5fc794998b4',
  'c59740e3-36e4-4263-8cde-5fe65ef308b4',
  '4becd2d2-73a4-4b5e-a145-80c07e39849a',
  '27f97232-81dc-402a-aa54-aaf18607f3e0',
  '224c16e6-7210-4344-8a81-e51dba633b90',
  'offer',
];

function stageRank(stageId?: string): number {
  const idx = STAGE_PIPELINE.indexOf((stageId || '').trim());
  return idx === -1 ? STAGE_PIPELINE.length : idx;
}

/** Posting id where there is one; the title only for links predating the required posting. */
function roleKey(link: InterviewLink): string {
  return link.postingId || `title:${link.jobTitle || link.name}`;
}

interface RoleGroup {
  /** The grouping key — posting id where there is one. Distinct per group even
   *  when two postings happen to share a job title, which `role` does not. */
  key: string;
  role: string;
  links: InterviewLink[];
}

/**
 * Links grouped by role, because one role runs several interviews (pre-screen,
 * personal, case, final) and they only make sense read together. Filters narrow
 * by TA owner, by role, and by free text over names, tags and interviewers.
 */
export function LinkList({
  links,
  onCreate,
  onEdit,
  onDeactivate,
  onReopen,
  onInvite,
  busyId,
}: {
  links: InterviewLink[];
  onCreate: () => void;
  onEdit: (link: InterviewLink) => void;
  onDeactivate: (link: InterviewLink) => void;
  onReopen: (link: InterviewLink) => void;
  onInvite: (link: InterviewLink) => void;
  busyId: string | null;
}) {
  const [owner, setOwner] = useState<string>('all');
  const [role, setRole] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const owners = useMemo(
    () => [...new Set(links.map((l) => l.taOwner || '').filter(Boolean))].sort(),
    [links]
  );
  const postingTitles = usePostingTitles(links);
  // The role filter and the headings use the same key and name, so the two cannot disagree.
  const roles = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const l of links) if (!byKey.has(roleKey(l))) byKey.set(roleKey(l), roleName(l, postingTitles));
    return [...byKey.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [links, postingTitles]);

  const groups = useMemo<RoleGroup[]>(() => {
    const needle = query.trim().toLowerCase();
    const filtered = links.filter((l) => {
      if (!showInactive && !l.active) return false;
      if (owner !== 'all' && (l.taOwner || '') !== owner) return false;
      if (role !== 'all' && roleKey(l) !== role) return false;
      if (!needle) return true;
      // Searching interviewers matters: "who is on the loop for X" is the
      // question a TA manager actually asks.
      const haystack = [
        l.name,
        roleName(l, postingTitles),
        l.jobTitle,
        l.stageName,
        l.taOwner || '',
        ...(l.interviewers ?? []).map((i) => i.email),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });

    /**
     * Grouped by Lever posting id, not by the job title.
     *
     * The title is free text that defaults to the link's own name, so a role's
     * pre-screen, case and final interviews only landed together when someone
     * typed the same string three times. They routinely did not, and the overview
     * then showed one "role" per interview — which is the whole point of grouping,
     * lost to a typo. The posting id is the same for every stage of a role.
     *
     * Keyed by title only as a fallback for links predating the required posting.
     */
    const byRole = new Map<string, InterviewLink[]>();
    for (const link of filtered) {
      const key = roleKey(link);
      byRole.set(key, [...(byRole.get(key) || []), link]);
    }
    return [...byRole.entries()]
      .map(([key, roleLinks]) => ({
        key,
        // Every link in a group shares the posting, so any of them names the role.
        role: roleName(roleLinks[0], postingTitles),
        links: roleLinks.sort(
          (a, b) =>
          stageRank(a.stageId) - stageRank(b.stageId) ||
          `${a.name ?? ''}`.localeCompare(`${b.name ?? ''}`)
        ),
      }))
      .sort((a, b) => a.role.localeCompare(b.role));
  }, [links, owner, role, query, showInactive, postingTitles]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="min-w-[200px] flex-1">
          <span className="mb-1 block text-xs font-medium text-gray-700">Search</span>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Role, stage or interviewer"
              className="pl-8"
            />
          </div>
        </label>

        <label>
          <span className="mb-1 block text-xs font-medium text-gray-700">TA manager</span>
          <select
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="all">All TA managers</option>
            {owners.map((o) => (
              <option key={o} value={o}>
                {personLabel(o)}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="mb-1 block text-xs font-medium text-gray-700">Role</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="max-w-[240px] rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="all">All roles</option>
            {roles.map(([key, name]) => (
              <option key={key} value={key}>
                {name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 pb-2 text-xs text-gray-700">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          Show closed
        </label>

        <Button className="mb-0.5" onClick={onCreate}>
          New link
        </Button>
      </div>

      {groups.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center">
          <p className="text-sm font-medium text-gray-700">No interview links</p>
          <p className="mt-1 text-xs text-gray-500">
            {links.length === 0
              ? 'Create one per role and stage — pre-screen, personal, case, final.'
              : 'Nothing matches these filters.'}
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <section key={group.key}>
              <header className="mb-2 flex items-baseline justify-between">
                <h2 className="text-sm font-semibold text-gray-900">{group.role}</h2>
                <span className="text-xs text-gray-500">
                  {group.links.length} {group.links.length === 1 ? 'interview' : 'interviews'}
                </span>
              </header>

              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                {group.links.map((link, idx) => (
                  <div
                    key={link.id}
                    className={cn(
                      'flex flex-wrap items-center gap-3 p-3.5',
                      idx > 0 && 'border-t border-gray-100',
                      !link.active && 'bg-gray-50'
                    )}
                  >
                    <div className="min-w-[200px] flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{link.stageName}</span>
                        <span className="rounded bg-[#E9EAEC] px-2 py-0.5 text-[11px] font-medium text-[#707070]">
                          {link.meetingType === 'panel' ? 'Panel' : '1:1'}
                        </span>
                        {link.audience === 'linkedin' ? (
                          // Never offered to applicants — the one thing a recruiter must not
                          // discover by wondering why the board says "no interview link".
                          <span
                            className="rounded bg-[#DBEBFC] px-2 py-0.5 text-[11px] font-medium text-[#0C447C]"
                            title="Only bookable through its LinkedIn link. Never offered to normal applicants."
                          >
                            LinkedIn only
                          </span>
                        ) : null}
                        {link.inviteEmail?.custom === true ? (
                          <span
                            className="rounded bg-[#FFF1DB] px-2 py-0.5 text-[11px] font-medium text-[#8A4B00]"
                            title="This link sends its own invite wording, not the Email Library template."
                          >
                            Custom wording
                          </span>
                        ) : null}
                        {link.feedbackCall?.enabled ? (
                          <span
                            className="rounded bg-[#DBEBFC] px-2 py-0.5 text-[11px] font-medium text-[#0072EA]"
                            title={`Rejected candidates can book a 15-min feedback call with ${(link.feedbackCall.interviewers ?? [])
                              .map((i) => personLabel(i.email))
                              .join(', ')}`}
                          >
                            Feedback call
                          </span>
                        ) : null}
                        {!link.active ? (
                          <span className="rounded bg-[#FCDFE4] px-2 py-0.5 text-[11px] font-medium text-[#791F1F]">
                            Closed
                          </span>
                        ) : link.roleClosed ? (
                          // Live here, but the posting is closed in Lever, so candidates are told the
                          // role is no longer open and no new invite can be minted. Re-opening the
                          // posting brings it back by itself — nothing to do on this page.
                          <span
                            className="rounded bg-[#FCEEE2] px-2 py-0.5 text-[11px] font-medium text-[#854F0B]"
                            title="The Lever posting is closed, so this link takes no new bookings. Already-booked interviews are unaffected."
                          >
                            Role closed in Lever
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {link.name} · {link.minutes} min · {personLabel(link.taOwner)}
                      </p>
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
                        <Users className="h-3 w-3 shrink-0" />
                        {(link.interviewers ?? []).length
                          ? (link.interviewers ?? []).map((i) => personLabel(i.email)).join(', ')
                          : 'no interviewer'}
                      </p>
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                      <LinkedInLinkActions link={link} />
                      <Button size="sm" onClick={() => onInvite(link)}>
                        <Send className="mr-1.5 h-3.5 w-3.5" />
                        Candidate links
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onEdit(link)}>
                        <Pencil className="mr-1.5 h-3.5 w-3.5" />
                        Edit
                      </Button>
                      {link.active ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onDeactivate(link)}
                          disabled={busyId === link.id}
                          title="Close this link — candidates who already have it will be told it is closed"
                        >
                          <Power className="mr-1.5 h-3.5 w-3.5" />
                          Close
                        </Button>
                      ) : (
                        /*
                          Closing used to be a one-way door: a closed link offered
                          only Edit, and editing preserved its closed state, so
                          nothing in the UI could bring it back. Reopening is a
                          soft flag, and the token is unchanged — links already
                          sent to candidates start working again, which is exactly
                          why it is a deliberate click and not a side effect of
                          saving an edit.
                        */
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onReopen(link)}
                          disabled={busyId === link.id}
                          title="Reopen this link — any candidate who still has it can book again"
                        >
                          <Power className="mr-1.5 h-3.5 w-3.5" />
                          Reopen
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
